import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { API } from '@/config';
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('whisper-token'));
  const [loading, setLoading] = useState(true);

  const setAuth = useCallback((tokenVal, userVal) => {
    setToken(tokenVal);
    setUser(userVal);
    if (tokenVal) {
      localStorage.setItem('whisper-token', tokenVal);
      axios.defaults.headers.common['Authorization'] = `Bearer ${tokenVal}`;
    } else {
      localStorage.removeItem('whisper-token');
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const setAuthFromGoogle = useCallback((tokenVal, userVal) => {
    setAuth(tokenVal, userVal);
  }, [setAuth]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(res => { setUser(res.data); setLoading(false); })
        .catch(() => { setAuth(null, null); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token, setAuth]);

  const signup = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/signup`, { email, password, name });
    setAuth(res.data.token, res.data.user);
    return res.data;
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setAuth(res.data.token, res.data.user);
    return res.data;
  };

  const logout = () => setAuth(null, null);

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) { /* Silently fail - user stays as-is */ }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, signup, login, logout, refreshUser, setAuthFromGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
}
